import React, { useMemo, useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";
import WebsiteInsightRecommendations from "./WebsiteInsightRecommendations";
import WebsiteTopCampaignInsightSlide from "./WebsiteTopCampaignInsightSlide";
import UnlockRemixAiPrompt from "./UnlockRemixAiPrompt";
import CFToggle from "../shared/forms/CFToggle";
import CFSelect from "../shared/forms/CFSelect";
import RemixSparkleIcon from "../variants/ai/RemixSparkleIcon";

const PDF_EXPORT_ACTION_COST = 10;

const SectionDivider = () => (
  <hr
    style={{
      border: 0,
      borderTop: "1px solid rgb(233, 235, 241)",
      margin: "0",
    }}
  />
);

const DEFAULT_EXPORT_SETTINGS = {
  custom_prompt: "",
  include_website_recommendations: true,
  include_campaign_insights: true,
  include_portfolio_metrics: true,
  include_next_steps: true,
  include_screenshot: true,
  required_campaigns: [],
  excluded_campaigns: [],
};

const CONTENT_TOGGLES = [
  {
    key: "include_website_recommendations",
    label: "Website recommendations",
  },
  {
    key: "include_campaign_insights",
    label: "Campaign insights",
  },
  {
    key: "include_portfolio_metrics",
    label: "Portfolio metrics",
  },
  {
    key: "include_next_steps",
    label: "Next steps",
  },
  {
    key: "include_screenshot",
    label: "Website screenshot",
  },
];

const normalizeExportSettings = (raw = {}) => {
  const source = raw || {};
  return {
    custom_prompt: source.custom_prompt || "",
    include_website_recommendations:
      source.include_website_recommendations !== false,
    include_campaign_insights: source.include_campaign_insights !== false,
    include_portfolio_metrics: source.include_portfolio_metrics !== false,
    include_next_steps: source.include_next_steps !== false,
    include_screenshot: source.include_screenshot !== false,
    required_campaigns: Array.isArray(source.required_campaigns)
      ? source.required_campaigns.map(Number).filter(Boolean)
      : [],
    excluded_campaigns: Array.isArray(source.excluded_campaigns)
      ? source.excluded_campaigns.map(Number).filter(Boolean)
      : [],
  };
};

const campaignOptionsForIds = (campaignOptions, ids) => {
  const idSet = new Set((ids || []).map(Number));
  return (campaignOptions || []).filter((option) =>
    idSet.has(Number(option.value))
  );
};

const POLL_INTERVAL_MS = 5000;

const WebsiteAiInsights = (props) => {
  const {
    websiteId,
    website,
    remixAiGated = false,
    aiDisabled = false,
    websiteSettingsUrl,
    exportableInsight = false,
    exportTriggerUrl,
    timeframeOptions = [],
    aiInsightsLoading: initialLoading = false,
    aiInsightsStatusUrl,
    websiteRecommendations: initialRecommendations,
    playbooksById: initialPlaybooksById = {},
    campaignSlides: initialCampaignSlides = [],
    analysisPeriod: initialAnalysisPeriod,
    currency,
    aiInsightsSettings = DEFAULT_EXPORT_SETTINGS,
    aiInsightsCampaignOptions = [],
  } = props;

  const aiEnabled = !aiDisabled;

  const [loading, setLoading] = useState(
    initialLoading && !initialRecommendations
  );
  const [snapshotReady, setSnapshotReady] = useState(exportableInsight);
  const [exportReady, setExportReady] = useState(exportableInsight);
  const [websiteRecommendations, setWebsiteRecommendations] = useState(
    initialRecommendations
  );
  const [playbooksById, setPlaybooksById] = useState(initialPlaybooksById);
  const [campaignSlides, setCampaignSlides] = useState(initialCampaignSlides);
  const [analysisPeriod, setAnalysisPeriod] = useState(initialAnalysisPeriod);
  const pollRef = useRef(null);

  useEffect(() => {
    if (!loading || !aiInsightsStatusUrl) return;

    const poll = async () => {
      try {
        const res = await fetch(aiInsightsStatusUrl, {
          headers: { Accept: "application/json" },
        });
        if (!res.ok) {
          clearInterval(pollRef.current);
          setLoading(false);
          return;
        }
        const data = await res.json();
        if (!data.loading) {
          clearInterval(pollRef.current);
          setLoading(false);
          setSnapshotReady(true);
          setExportReady(true);
          setWebsiteRecommendations(data.websiteRecommendations || null);
          setPlaybooksById(data.playbooksById || {});
          setCampaignSlides(data.campaignSlides || []);
          setAnalysisPeriod(data.analysisPeriod || null);
        }
      } catch (_) {
        // transient network error — keep polling
      }
    };

    poll();
    pollRef.current = setInterval(poll, POLL_INTERVAL_MS);
    return () => clearInterval(pollRef.current);
  }, [loading, aiInsightsStatusUrl]);

  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [exportStatus, setExportStatus] = useState("idle"); // idle|submitting|error|queued
  const [exportTimeframe, setExportTimeframe] = useState("last_week");
  const [exportStartDate, setExportStartDate] = useState("");
  const [exportEndDate, setExportEndDate] = useState("");
  const [exportSettings, setExportSettings] = useState(() =>
    normalizeExportSettings(aiInsightsSettings)
  );

  const exportTimeframeOptions = useMemo(() => {
    const options = Array.isArray(timeframeOptions) ? timeframeOptions : [];
    if (options.some((option) => option?.value === "last_week")) {
      return options;
    }
    return [{ value: "last_week", label: "Last Week" }, ...options];
  }, [timeframeOptions]);

  const campaignSelectOptions = useMemo(
    () =>
      (aiInsightsCampaignOptions || []).map((option) => ({
        value: option.value,
        label: option.label || option.text,
        text: option.label || option.text,
      })),
    [aiInsightsCampaignOptions]
  );

  const selectedRequiredCampaigns = useMemo(
    () =>
      campaignOptionsForIds(
        campaignSelectOptions,
        exportSettings.required_campaigns
      ),
    [campaignSelectOptions, exportSettings.required_campaigns]
  );

  const selectedExcludedCampaigns = useMemo(
    () =>
      campaignOptionsForIds(
        campaignSelectOptions,
        exportSettings.excluded_campaigns
      ),
    [campaignSelectOptions, exportSettings.excluded_campaigns]
  );

  const hasWebsiteSection = Boolean(websiteRecommendations);
  const hasCampaignSections = campaignSlides.length > 0;
  const noCampaigns = snapshotReady && !loading && !hasCampaignSections;
  const hasContent = hasWebsiteSection || hasCampaignSections;

  const openExportModal = () => {
    if (!exportReady) return;
    setExportStatus("idle");
    setExportTimeframe("last_week");
    setExportStartDate("");
    setExportEndDate("");
    setExportSettings(normalizeExportSettings(aiInsightsSettings));
    setExportModalOpen(true);
  };

  const updateExportSetting = (key, value) => {
    setExportSettings((current) => ({
      ...current,
      [key]: value,
    }));
    setExportStatus("idle");
  };

  const submitExport = async () => {
    if (!exportTriggerUrl || exportStatus === "submitting") return;

    setExportStatus("submitting");
    try {
      if (
        exportTimeframe === "custom" &&
        (!exportStartDate || !exportEndDate)
      ) {
        setExportStatus("error");
        return;
      }

      const endpoint = `/websites/${websiteId}/insights/ai_insights_export`;
      const body = new URLSearchParams();
      body.set("timeframe", exportTimeframe);
      if (exportTimeframe === "custom") {
        body.set("start_date", exportStartDate);
        body.set("end_date", exportEndDate);
      }
      body.set(
        "settings",
        JSON.stringify({
          ...exportSettings,
          required_campaigns: exportSettings.required_campaigns,
          excluded_campaigns: exportSettings.excluded_campaigns,
        })
      );
      const csrfToken =
        document
          .querySelector('meta[name="csrf-token"]')
          ?.getAttribute("content") || "";

      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
          "X-CSRF-Token": csrfToken,
        },
        body,
      });

      if (!response.ok) {
        throw new Error(`Export request failed with status ${response.status}`);
      }

      setExportStatus("queued");
    } catch (error) {
      setExportStatus("error");
    }
  };

  return (
    <div className="WebsiteAiInsights">
      <div
        className="flex items-center justify-between gap-3"
        style={{
          padding: "13px 0 10px",
          borderBottom: hasContent ? "1px solid rgb(233, 235, 241)" : "none",
        }}
      >
        <div style={{ flex: "1 1 auto", minWidth: 0 }}>
          <h4 className="bold mb-0">AI Insights</h4>
          <p className="insight-description light text-xl mb-0 mt-1">
            ConvertFlow AI&apos;s recommendations based on last week
          </p>
        </div>
        {exportTriggerUrl &&
          (remixAiGated ? (
            <button
              type="button"
              className="upgrade-remix-ai-button btn toolbar-btn field-title !border-slate-200"
              style={{
                background: "#fff",
                whiteSpace: "nowrap",
                flexShrink: 0,
              }}
              title="Unlock Remix AI to export AI insights"
            >
              <span
                className="glyphicon glyphicon-save"
                style={{ fontSize: "12px", marginRight: "5px" }}
              ></span>
              Export
            </button>
          ) : (
            <button
              type="button"
              className="btn toolbar-btn field-title !border-slate-200"
              style={{
                background: "#fff",
                whiteSpace: "nowrap",
                flexShrink: 0,
              }}
              disabled={!exportReady}
              title={
                exportReady
                  ? "Generate AI insights report as PDF"
                  : "AI insights will be ready to export once the weekly snapshot is generated."
              }
              onClick={openExportModal}
            >
              <span
                className="glyphicon glyphicon-save"
                style={{ fontSize: "12px", marginRight: "5px" }}
              ></span>
              Export
            </button>
          ))}
      </div>

      {exportModalOpen &&
        createPortal(
          <div
            className="modal fade in"
            role="dialog"
            style={{ display: "block" }}
            aria-modal="true"
            onClick={(e) => {
              if (e.target === e.currentTarget) setExportModalOpen(false);
            }}
          >
            <div
              className="modal-dialog"
              role="document"
              style={{ maxWidth: "560px" }}
            >
              <div className="modal-content smooth">
                <div
                  className="modal-header"
                  style={{ paddingLeft: "30px", paddingRight: "30px" }}
                >
                  <button
                    type="button"
                    className="close"
                    aria-label="Close"
                    onClick={() => setExportModalOpen(false)}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="17"
                      height="17"
                      viewBox="0 0 17 17"
                      fill="none"
                      aria-hidden="true"
                      focusable="false"
                    >
                      <path
                        d="M12.2996 4.1001L4.09961 12.3001M12.2996 12.3001L4.09961 4.1001"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                      />
                    </svg>
                  </button>
                  <h4 className="modal-title bold">Export AI Insights</h4>
                </div>
                <div
                  className="modal-body"
                  style={{
                    maxHeight: "calc(100vh - 140px)",
                    overflowY: "auto",
                    paddingLeft: "30px",
                    paddingRight: "30px",
                    paddingBottom: "50px",
                  }}
                >
                  <div
                    className="row some-vertical-space"
                    style={{ marginTop: 0 }}
                  >
                    <div className="col-xs-12 mb-10">
                      <p className="field-title">Timeframe</p>
                      <select
                        className="text-field"
                        value={exportTimeframe}
                        onChange={(e) => {
                          setExportTimeframe(e.target.value);
                          setExportStatus("idle");
                        }}
                      >
                        {exportTimeframeOptions.map((option) => (
                          <option
                            key={`ai-insights-export-timeframe-${option.value}`}
                            value={option.value}
                          >
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    {exportTimeframe === "custom" && (
                      <>
                        <div className="col-xs-6 mb-10">
                          <p className="field-title">Start date</p>
                          <input
                            type="date"
                            className="text-field"
                            value={exportStartDate}
                            onChange={(e) => setExportStartDate(e.target.value)}
                          />
                        </div>
                        <div className="col-xs-6 mb-10">
                          <p className="field-title">End date</p>
                          <input
                            type="date"
                            className="text-field"
                            value={exportEndDate}
                            onChange={(e) => setExportEndDate(e.target.value)}
                          />
                        </div>
                      </>
                    )}

                    <div className="col-xs-12 mb-10">
                      <p className="field-title">Custom prompt instructions</p>
                      <textarea
                        className="text-field"
                        rows={4}
                        placeholder="Optional instructions for this PDF generation"
                        value={exportSettings.custom_prompt}
                        onChange={(e) =>
                          updateExportSetting("custom_prompt", e.target.value)
                        }
                      />
                    </div>

                    <div className="col-xs-12 mb-10">
                      {CONTENT_TOGGLES.map((toggle) => (
                        <div
                          key={toggle.key}
                          className="flex items-center justify-between gap-3 mb-3"
                        >
                          <span className="text-xl light mb-0">
                            {toggle.label}
                          </span>
                          <CFToggle
                            checked={exportSettings[toggle.key] === true}
                            onChange={(checked) =>
                              updateExportSetting(toggle.key, checked)
                            }
                          />
                        </div>
                      ))}
                    </div>

                    <div className="col-xs-12 mb-10">
                      <p className="field-title">Required campaigns</p>
                      <CFSelect
                        options={campaignSelectOptions}
                        value={selectedRequiredCampaigns}
                        onChange={(selected) =>
                          updateExportSetting(
                            "required_campaigns",
                            (selected || []).map((option) =>
                              Number(option.value)
                            )
                          )
                        }
                        isMulti={true}
                        isClearable={true}
                        placeholder="Always include these campaigns..."
                      />
                    </div>

                    <div className="col-xs-12 mb-10">
                      <p className="field-title">Excluded campaigns</p>
                      <CFSelect
                        options={campaignSelectOptions}
                        value={selectedExcludedCampaigns}
                        onChange={(selected) =>
                          updateExportSetting(
                            "excluded_campaigns",
                            (selected || []).map((option) =>
                              Number(option.value)
                            )
                          )
                        }
                        isMulti={true}
                        isClearable={true}
                        placeholder="Exclude these campaigns..."
                      />
                    </div>

                    {exportStatus === "queued" && (
                      <div className="col-xs-12 mb-10">
                        <p className="text-xl light text-slate-500 mb-0">
                          Your AI insights export is being generated. You will
                          be notified shortly.
                        </p>
                      </div>
                    )}

                    {exportStatus === "error" && (
                      <div className="col-xs-12 mb-10">
                        <p className="text-xl light text-red-600 mb-0">
                          Export failed. Please try again.
                        </p>
                      </div>
                    )}

                    <div className="col-xs-12">
                      <button
                        type="button"
                        className={`btn btn-block !text-xl font-semibold ${
                          aiEnabled
                            ? "border-0 !text-white hover:!text-white focus:!text-white flex items-center justify-center gap-3"
                            : "blue-button"
                        }`}
                        style={
                          aiEnabled
                            ? {
                                background:
                                  "linear-gradient(296deg, #003FFF -40.28%, #6B8CFF 110.6%)",
                              }
                            : {}
                        }
                        onClick={submitExport}
                        disabled={
                          exportStatus === "submitting" ||
                          exportStatus === "queued"
                        }
                      >
                        {aiEnabled && exportStatus !== "submitting" && (
                          <RemixSparkleIcon
                            color="#fff"
                            width={14}
                            height={15}
                          />
                        )}
                        {exportStatus === "submitting"
                          ? "Exporting…"
                          : "Generate PDF"}
                      </button>
                      {aiEnabled && (
                        <p className="text-lg light text-slate-500 mb-0 mt-2">
                          AI insights PDF generation costs{" "}
                          {PDF_EXPORT_ACTION_COST} actions
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}

      {!hasContent && (
        <div className="py-5">
          {remixAiGated ? (
            <UnlockRemixAiPrompt website={website} />
          ) : aiDisabled ? (
            <p className="text-xl mb-0 light">
              AI is disabled for this website.{" "}
              {websiteSettingsUrl && (
                <a href={websiteSettingsUrl} className="text-xl">
                  Enable in website settings →
                </a>
              )}
            </p>
          ) : loading ? (
            <div
              className="flex items-center gap-2 rounded-lg px-4 py-3 mb-4"
              style={{
                background:
                  "linear-gradient(white, white) padding-box, linear-gradient(90deg, #6B8CFF 0%, #003FFF 100%) border-box",
                border: "1px solid transparent",
              }}
            >
              <span
                className="glyphicon glyphicon-refresh"
                style={{
                  animation: "spin 1s linear infinite",
                  fontSize: "13px",
                  color: "#003FFF",
                }}
              />
              <p className="text-xl mb-0 light" style={{ color: "#003FFF" }}>
                Generating AI insights…
              </p>
            </div>
          ) : (
            <p className="text-xl mb-0 light">
              AI insights will appear here once weekly recommendations are
              generated.
            </p>
          )}
        </div>
      )}

      {hasWebsiteSection && (
        <WebsiteInsightRecommendations
          embedded
          websiteId={websiteId}
          website={website}
          remixAiGated={remixAiGated}
          noCampaigns={noCampaigns}
          recommendations={websiteRecommendations}
          playbooksById={playbooksById}
          overviewTitle="Website Overview"
        />
      )}

      {hasCampaignSections &&
        campaignSlides.map((slide, index) => (
          <React.Fragment key={`ai-insights-campaign-${slide.id || index}`}>
            <SectionDivider />
            <WebsiteTopCampaignInsightSlide
              embedded
              slide={slide}
              currency={currency}
              analysisPeriod={analysisPeriod}
              website={website}
            />
          </React.Fragment>
        ))}
    </div>
  );
};

WebsiteAiInsights.propTypes = {
  websiteId: PropTypes.number.isRequired,
  website: PropTypes.object,
  remixAiGated: PropTypes.bool,
  exportableInsight: PropTypes.bool,
  exportTriggerUrl: PropTypes.string,
  timeframeOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string,
    })
  ),
  aiDisabled: PropTypes.bool,
  websiteSettingsUrl: PropTypes.string,
  aiInsightsLoading: PropTypes.bool,
  aiInsightsStatusUrl: PropTypes.string,
  websiteRecommendations: PropTypes.object,
  playbooksById: PropTypes.object,
  campaignSlides: PropTypes.arrayOf(PropTypes.object),
  analysisPeriod: PropTypes.shape({
    start: PropTypes.string,
    end: PropTypes.string,
  }),
  currency: PropTypes.string,
  aiInsightsSettings: PropTypes.object,
  aiInsightsCampaignOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      label: PropTypes.string,
      text: PropTypes.string,
    })
  ),
};

const style = document.createElement("style");
style.textContent =
  "@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }";
document.head.appendChild(style);

export default WebsiteAiInsights;
